import { appelApi } from './client';
import type {
  ModificationEvenement,
  ModificationRappel,
  ModificationTache,
} from '@colette/shared';
import type {
  Cercle,
  ChecklistPayload,
  ElementBoite,
  ElementChecklist,
  EtatGoogle,
  EventPayload,
  Fait,
  FaitPayload,
  Foyer,
  MembreFoyer,
  Proposition,
  ReminderPayload,
  ReponseAVenir,
  ReponseConnexion,
  ReponseMeteo,
  ReponseOrganiser,
  ReponsePlanning,
  ReponseRepas,
  ResultatGoogle,
  RoleMembre,
  Routine,
  SujetDeFait,
  TaskPayload,
  UsageDuFoyer,
  Utilisateur,
} from './types';

// Un seul endroit ou les chemins de l'API sont ecrits. Un ecran qui appelle
// `api.planning()` ne peut pas se tromper d'URL ni oublier le jeton.

export const api = {
  connexion: (email: string, motDePasse: string) =>
    appelApi<ReponseConnexion>('/auth/connexion', {
      methode: 'POST',
      corps: { email, password: motDePasse },
      // Sans jeton : c'est l'appel qui en produit un. Voir la garde du 401 dans client.ts.
      authentifie: false,
    }),

  inscription: (nom: string, email: string, motDePasse: string) =>
    appelApi<ReponseConnexion>('/auth/inscription', {
      methode: 'POST',
      corps: { displayName: nom, email, password: motDePasse },
      authentifie: false,
    }),

  /** Demande un lien de reinitialisation. Repond TOUJOURS pareil, compte ou non. */
  demanderReinitialisation: (email: string) =>
    appelApi<void>('/auth/mot-de-passe-oublie', {
      methode: 'POST',
      corps: { email },
      authentifie: false,
    }),

  /** Utilise le lien recu par email. Coupe toutes les sessions du compte. */
  reinitialiser: (token: string, newPassword: string) =>
    appelApi<void>('/auth/reinitialiser', {
      methode: 'POST',
      corps: { token, newPassword },
      authentifie: false,
    }),

  moi: (signal?: AbortSignal) => appelApi<{ user: Utilisateur }>('/auth/moi', { signal }),

  creerFoyer: (nom: string, fuseau: string) =>
    appelApi<{ household: { id: string; name: string; timezone: string } }>('/foyers', {
      methode: 'POST',
      corps: { name: nom, timezone: fuseau },
    }),

  deconnexion: () => appelApi<void>('/auth/deconnexion', { methode: 'POST' }),

  foyer: (signal?: AbortSignal) => appelApi<{ household: Foyer }>('/foyers/moi', { signal }),

  /** Le plan du foyer, et ce qu'il a utilise ce mois-ci. Aucun appel d'IA. */
  plan: (signal?: AbortSignal) => appelApi<UsageDuFoyer>('/foyers/plan', { signal }),

  /**
   * Ajoute quelqu'un qui UTILISERA Colette. Fournir email + mot de passe lui cree un
   * compte ; les omettre inscrit un membre que Colette connait sans qu'il se connecte.
   */
  ajouterMembre: (membre: {
    displayName: string;
    role: RoleMembre;
    birthDate?: string;
    email?: string;
    password?: string;
  }) => appelApi<{ member: MembreFoyer }>('/foyers/membres', { methode: 'POST', corps: membre }),

  /** Ce que Colette sait du foyer : les faits visibles par moi (cercles compris). */
  faits: (signal?: AbortSignal) => appelApi<{ faits: Fait[] }>('/faits', { signal }),

  retenirUnFait: (fait: { sujet: SujetDeFait; texte: string }) =>
    appelApi<{ fait: Fait }>('/faits', { methode: 'POST', corps: fait }),

  /** Corriger, pas re-saisir : seul le texte change, le sujet ne bouge pas. */
  corrigerUnFait: (id: string, texte: string) =>
    appelApi<{ fait: Fait }>(`/faits/${encodeURIComponent(id)}`, {
      methode: 'PATCH',
      corps: { texte },
    }),

  oublierUnFait: (id: string) =>
    appelApi<void>(`/faits/${encodeURIComponent(id)}`, { methode: 'DELETE' }),

  /** Ajoute quelqu'un que Colette doit seulement connaitre par son nom. */
  ajouterPersonne: (personne: { displayName: string; birthDate?: string }) =>
    appelApi<{ person: { id: string; displayName: string } }>('/foyers/personnes', {
      methode: 'POST',
      corps: personne,
    }),

  /** Corrige la fiche d'une personne. `birthDate: null` efface la date. */
  modifierPersonne: (id: string, fiche: { displayName?: string; birthDate?: string | null }) =>
    appelApi<{ person: { id: string } }>(`/foyers/personnes/${encodeURIComponent(id)}`, {
      methode: 'PATCH',
      corps: fiche,
    }),

  retirerPersonne: (id: string) =>
    appelApi<void>(`/foyers/personnes/${encodeURIComponent(id)}`, { methode: 'DELETE' }),

  /** Corrige la fiche d'un membre (prenom, date de naissance). */
  modifierMembre: (id: string, fiche: { displayName?: string; birthDate?: string | null }) =>
    appelApi<{ member: { id: string } }>(`/foyers/membres/${encodeURIComponent(id)}`, {
      methode: 'PATCH',
      corps: fiche,
    }),

  /**
   * Repare l'acces d'un compte ENFANT : nouvelle adresse et/ou nouveau mot de passe.
   * Le serveur refuse (403) pour un compte adulte.
   */
  changerIdentifiantsEnfant: (
    membreId: string,
    identifiants: { email?: string; password?: string },
  ) =>
    appelApi<{ member: { id: string; email: string } }>(
      `/foyers/membres/${encodeURIComponent(membreId)}/acces`,
      { methode: 'PATCH', corps: identifiants },
    ),

  /** Retire un membre SANS compte ; l'API refuse (409) si le membre en a un. */
  retirerMembre: (id: string) =>
    appelApi<void>(`/foyers/membres/${encodeURIComponent(id)}`, { methode: 'DELETE' }),

  /**
   * Change mon mot de passe. Le serveur exige l'ACTUEL et revoque les autres sessions.
   * Ce n'est pas une reinitialisation : un mot de passe oublie reste sans recours.
   */
  changerMotDePasse: (currentPassword: string, newPassword: string) =>
    appelApi<void>('/auth/mot-de-passe', {
      methode: 'POST',
      corps: { currentPassword, newPassword },
    }),

  /** Change MON nom d'affichage — le serveur met aussi a jour ma fiche de membre. */
  changerMonNom: (displayName: string) =>
    appelApi<{ user: Utilisateur }>('/moi', { methode: 'PATCH', corps: { displayName } }),

  /** Renomme le foyer. Reserve aux adultes cote serveur. */
  renommerFoyer: (name: string) =>
    appelApi<{ household: { id: string; name: string } }>('/foyers/nom', {
      methode: 'PATCH',
      corps: { name },
    }),

  /** Tout ce que Colette sait de moi, en un objet. Les cercles s'appliquent. */
  exporterMesDonnees: () => appelApi<Record<string, unknown>>('/moi/export'),

  /** Partir pour de bon. Irreversible — le mot de passe le confirme. */
  supprimerMonCompte: (password: string) =>
    appelApi<{ foyerSupprime: boolean }>('/moi', { methode: 'DELETE', corps: { password } }),

  boite: (signal?: AbortSignal) => appelApi<{ items: ElementBoite[] }>('/boite', { signal }),

  /** Efface un message de la Boite. Ce qui a deja ete valide RESTE au planning. */
  supprimerMessage: (id: string) =>
    appelApi<void>(`/boite/${encodeURIComponent(id)}`, { methode: 'DELETE' }),

  deposer: (texte: string) =>
    appelApi<{ item: ElementBoite }>('/boite', { methode: 'POST', corps: { text: texte } }),

  /** Depose une PHOTO (deja reduite par preparerPhoto), avec une legende optionnelle. */
  deposerPhoto: (photo: {
    imageBase64: string;
    imageMimeType: 'image/jpeg' | 'image/png' | 'image/webp';
    text?: string;
  }) => appelApi<{ item: ElementBoite }>('/boite', { methode: 'POST', corps: photo }),

  /** ATTENTION : cet appel declenche l'IA et il est FACTURE. Jamais en boucle. */
  organiser: (idElement: string) =>
    appelApi<ReponseOrganiser>(`/boite/${encodeURIComponent(idElement)}/organiser`, {
      methode: 'POST',
    }),

  /** ATTENTION : rejoue la comprehension, donc appel IA FACTURE. Une fois par reponse. */
  preciser: (idElement: string, reponse: string) =>
    appelApi<ReponseOrganiser>(`/boite/${encodeURIComponent(idElement)}/preciser`, {
      methode: 'POST',
      corps: { reponse },
    }),

  propositions: (signal?: AbortSignal) =>
    appelApi<{ proposals: Proposition[] }>('/propositions', { signal }),

  /**
   * Valide une proposition ; sans cercle, la chose creee est pour tout le foyer.
   * `google` n'est present que pour un EVENEMENT : il dit ce que Google a pu faire.
   */
  valider: (idProposition: string, cercle?: Cercle) =>
    appelApi<{ proposal: Proposition; google?: ResultatGoogle }>(
      `/propositions/${encodeURIComponent(idProposition)}/valider`,
      { methode: 'POST', corps: cercle === undefined ? undefined : { cercle } },
    ),

  /**
   * Corrige PUIS applique une proposition (le serveur valide le contenu par le schema
   * partage). Gratuit : aucun appel d'intelligence artificielle, contrairement a une
   * nouvelle dictee.
   */
  corriger: (
    idProposition: string,
    payload: EventPayload | TaskPayload | ReminderPayload | ChecklistPayload | FaitPayload,
    cercle?: Cercle,
  ) =>
    appelApi<{ proposal: Proposition; google?: ResultatGoogle }>(
      `/propositions/${encodeURIComponent(idProposition)}/corriger`,
      { methode: 'POST', corps: { payload, ...(cercle === undefined ? {} : { cercle }) } },
    ),

  /** Change le cercle d'une chose existante — reserve a son createur (404 sinon). */
  changerCercle: (
    genre: 'evenement' | 'tache' | 'rappel' | 'checklist',
    id: string,
    cercle: Cercle,
  ) => {
    const chemins = {
      evenement: 'evenements',
      tache: 'taches',
      rappel: 'rappels',
      checklist: 'checklists',
    } as const;
    return appelApi<{ cercle: Cercle }>(
      `/${chemins[genre]}/${encodeURIComponent(id)}/cercle`,
      { methode: 'PATCH', corps: cercle },
    );
  },

  refuser: (idProposition: string) =>
    appelApi<{ proposal: Proposition }>(
      `/propositions/${encodeURIComponent(idProposition)}/refuser`,
      { methode: 'POST' },
    ),

  /** Ou en est la connexion Google du compte. */
  etatGoogle: (signal?: AbortSignal) => appelApi<EtatGoogle>('/connexions/google', { signal }),

  /** Rend l'URL de la page d'autorisation Google ; l'ecran y navigue lui-meme. */
  demarrerGoogle: () =>
    appelApi<{ url: string }>('/connexions/google/demarrer', { methode: 'POST' }),

  deconnecterGoogle: () => appelApi<void>('/connexions/google', { methode: 'DELETE' }),

  /** Coupe ou rallume l'ajout des evenements valides dans Google Agenda. */
  definirEcritureGoogle: (actif: boolean) =>
    appelApi<{ ecritureActive: boolean }>('/connexions/google/ecriture', {
      methode: 'PATCH',
      corps: { actif },
    }),

  /** Change le fuseau du foyer. Reserve aux adultes cote serveur. */
  definirFuseau: (timezone: string) =>
    appelApi<{ household: { id: string; name: string; timezone: string } }>('/foyers/fuseau', {
      methode: 'PATCH',
      corps: { timezone },
    }),

  /** Definit la ville du foyer ; le serveur la geocode et rend le nom retenu. */
  definirVille: (city: string) =>
    appelApi<{ household: { id: string; city: string } }>('/foyers/ville', {
      methode: 'PATCH',
      corps: { city },
    }),

  /** Previsions pour la ville du foyer. 404 SANS_VILLE tant qu'elle n'est pas definie. */
  meteo: (signal?: AbortSignal) => appelApi<ReponseMeteo>('/meteo', { signal }),

  /** Coche ou decoche une tache. */
  cocherTache: (id: string, fait: boolean) =>
    appelApi<{ fait: boolean }>(`/taches/${encodeURIComponent(id)}`, {
      methode: 'PATCH',
      corps: { fait },
    }),

  /** Ajoute un element a une liste, a la main — ecriture directe, aucun appel IA. */
  ajouterElement: (checklistId: string, label: string) =>
    appelApi<{ element: ElementChecklist }>(
      `/checklists/${encodeURIComponent(checklistId)}/elements`,
      { methode: 'POST', corps: { label } },
    ),

  /** Retire un element d'une liste. Deux gestes cote ecran : c'est destructeur. */
  supprimerElement: (id: string) =>
    appelApi<void>(`/checklists/elements/${encodeURIComponent(id)}`, { methode: 'DELETE' }),

  /** Coche ou decoche un element de liste. */
  cocherElement: (id: string, fait: boolean) =>
    appelApi<{ fait: boolean }>(`/checklists/elements/${encodeURIComponent(id)}`, {
      methode: 'PATCH',
      corps: { fait },
    }),

  /** La cle publique VAPID : ce que le navigateur presente au service push. */
  clePubliqueNotifications: () => appelApi<{ cle: string }>('/notifications/cle-publique'),

  /** Enregistre l'abonnement push de CE navigateur. */
  abonnerNotifications: (abonnement: { endpoint: string; p256dh: string; auth: string }) =>
    appelApi<void>('/notifications/abonnement', { methode: 'POST', corps: abonnement }),

  desabonnerNotifications: (endpoint: string) =>
    appelApi<void>('/notifications/abonnement', { methode: 'DELETE', corps: { endpoint } }),

  /** Envoie une notification d'essai vers les appareils du compte. 409 SANS_ABONNEMENT sinon. */
  essaiNotifications: () =>
    appelApi<{ envoyes: number }>('/notifications/essai', { methode: 'POST' }),

  /**
   * Modifie un evenement existant. N'envoyer QUE ce qui change : le serveur reconstruit
   * le reste depuis l'existant, dans le fuseau du foyer.
   */
  modifierEvenement: (id: string, modification: ModificationEvenement) =>
    appelApi<{ evenement: { id: string; title: string }; google?: ResultatGoogle }>(
      `/evenements/${encodeURIComponent(id)}`,
      { methode: 'PATCH', corps: modification },
    ),

  /** Modifie le contenu d'une tache (titre, echeance). `/contenu` : cocher garde `/taches/:id`. */
  modifierTache: (id: string, modification: ModificationTache) =>
    appelApi<{ tache: { id: string; title: string } }>(
      `/taches/${encodeURIComponent(id)}/contenu`,
      { methode: 'PATCH', corps: modification },
    ),

  supprimerTache: (id: string) =>
    appelApi<void>(`/taches/${encodeURIComponent(id)}`, { methode: 'DELETE' }),

  modifierRappel: (id: string, modification: ModificationRappel) =>
    appelApi<{ rappel: { id: string; message: string } }>(`/rappels/${encodeURIComponent(id)}`, {
      methode: 'PATCH',
      corps: modification,
    }),

  supprimerRappel: (id: string) =>
    appelApi<void>(`/rappels/${encodeURIComponent(id)}`, { methode: 'DELETE' }),

  renommerListe: (id: string, titre: string) =>
    appelApi<{ liste: { id: string; titre: string } }>(`/checklists/${encodeURIComponent(id)}`, {
      methode: 'PATCH',
      corps: { title: titre },
    }),

  supprimerListe: (id: string) =>
    appelApi<void>(`/checklists/${encodeURIComponent(id)}`, { methode: 'DELETE' }),

  /** Renomme un element. `/libelle` : cocher garde `/checklists/elements/:id`. */
  renommerElement: (id: string, label: string) =>
    appelApi<{ element: { id: string; label: string } }>(
      `/checklists/elements/${encodeURIComponent(id)}/libelle`,
      { methode: 'PATCH', corps: { label } },
    ),

  /** Supprime un evenement — et le retire de Google s'il y avait ete ecrit. */
  supprimerEvenement: (id: string) =>
    appelApi<{ google?: ResultatGoogle }>(`/evenements/${encodeURIComponent(id)}`, {
      methode: 'DELETE',
    }),

  /** Annule UN jour d'une serie recurrente — reserve a son createur (404 sinon). */
  annulerOccurrence: (serieId: string, date: string) =>
    appelApi<{ annulee: string }>(
      `/evenements/${encodeURIComponent(serieId)}/occurrences/annuler`,
      { methode: 'POST', corps: { date } },
    ),

  /** La semaine type. Aucun appel d'IA sur ce chemin. */
  routines: (signal?: AbortSignal) =>
    appelApi<{ routines: Routine[] }>('/routines', { signal }),

  /** Pose une routine a la main — gratuit, comme poser un plat. */
  creerRoutine: (routine: {
    title: string;
    startTime: string;
    endTime: string | null;
    days: number[];
  }) => appelApi<{ routine: Routine }>('/routines', { methode: 'POST', corps: routine }),

  /** Corrige une routine. N'envoyer QUE ce qui change. */
  modifierRoutine: (id: string, modification: Record<string, unknown>) =>
    appelApi<{ routine: Routine }>(`/routines/${encodeURIComponent(id)}`, {
      methode: 'PATCH',
      corps: modification,
    }),

  /** Les 14 jours de cuisine (semaine courante + suivante), filtres par cercles. */
  repas: (signal?: AbortSignal) => appelApi<ReponseRepas>('/repas', { signal }),

  /** Pose (ou remplace) le plat d'une case. Ecriture directe, aucun appel IA. */
  poserRepas: (repas: { date: string; creneau: 'DEJEUNER' | 'DINER'; titre: string }) =>
    appelApi<{ repas: { id: string; titre: string } }>('/repas', {
      methode: 'PUT',
      corps: repas,
    }),

  viderRepas: (caseRepas: { date: string; creneau: 'DEJEUNER' | 'DINER' }) =>
    appelApi<void>('/repas', { methode: 'DELETE', corps: caseRepas }),

  /** ATTENTION : appel IA FACTURE. Cree une proposition de liste a valider. */
  listeCoursesDeLaSemaine: (semaine: string) =>
    appelApi<{ item: { id: string } }>('/repas/liste-courses', {
      methode: 'POST',
      corps: { semaine },
    }),

  /** Ce que Colette surveille au-dela de la fenetre du planning, trie par echeance. */
  aVenir: (depuis: string, signal?: AbortSignal) =>
    appelApi<ReponseAVenir>(`/planning/a-venir?depuis=${encodeURIComponent(depuis)}`, { signal }),

  planning: (bornes: { du?: string; au?: string } = {}, signal?: AbortSignal) => {
    const parametres = new URLSearchParams();
    if (bornes.du !== undefined) parametres.set('from', bornes.du);
    if (bornes.au !== undefined) parametres.set('to', bornes.au);
    const requete = parametres.toString();
    return appelApi<ReponsePlanning>(`/planning${requete === '' ? '' : `?${requete}`}`, { signal });
  },
};
